import {
    sortByNameLabel,
    sortByDeletedDateLabel,
    sortByRecentlyAddedLabel,
    sortByFirstNameLabel,
    sortByLastNameLabel,
    sortByCompanyLabel,
    sortByHomeCityLabel,
    sortByWorkCityLabel,
} from './personaListFilter.locstring.json';
import { groupsFolder } from 'owa-locstrings/lib/strings/groupsfolder.locstring.json';
import { allLabel } from 'owa-locstrings/lib/strings/alllabel.locstring.json';
import {
    showPeopleLabel,
    filterLabel,
    sortOrderLabel,
    showContactListsLabel,
} from '../../../strings.locstring.json';
import loc from 'owa-localize';
import type { PersonaListViewModel } from '../../../models/PersonaListViewModel';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';

import { Filter, Sorting } from '../../../../appstate';
import { toFilterName } from '../../../utils/personaListFilterUtils';
import {
    createIContextualMenuItem,
    getFilterAriaLabel,
    getSortByAriaLabel,
} from '../../../utils/listFilterUtils';
import { isConsumer } from '../../../../shared';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import type DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';

function createSearchOptionsItems(
    viewState: PersonaListViewModel,
    onFilterChanged: (filter: Filter) => void
): IContextualMenuItem[] {
    const searchOption = (filter: Filter) => {
        return createIContextualMenuItem(
            toFilterName(filter),
            viewState.filter === filter,
            () => onFilterChanged(filter),
            getFilterAriaLabel
        );
    };

    const items: IContextualMenuItem[] = [];

    if (!isConsumer()) {
        items.push(searchOption(Filter.HubPerson));
    }

    items.push(searchOption(Filter.Contact));

    if (isGroupsEnabled()) {
        items.push(
            searchOption(Filter.Organization),
            { key: 'GroupsDivider', itemType: ContextualMenuItemType.Divider },
            searchOption(Filter.Group),
            searchOption(Filter.JoinedGroup),
            searchOption(Filter.OwnedGroup)
        );
    }

    if (!isConsumer()) {
        items.push(
            { key: 'ListsDivider', itemType: ContextualMenuItemType.Divider },
            searchOption(Filter.List)
        );
    }

    items.push(searchOption(Filter.PrivateDistributionList));

    if (!isConsumer()) {
        items.push(searchOption(Filter.PublicDistributionList));
    }

    return items.filter(item => !!item);
}

export function createHubListOptionsItems(
    viewState: PersonaListViewModel,
    isInSearch: boolean,
    isInDeletedItems: boolean,
    hideFilterFromDropDown: boolean,
    showGroupsFromDropDown: boolean,
    onFilterChanged: (filter: Filter) => void,
    onSortingChanged: (sorting: Sorting) => void,
    onDisplayOrderChanged: (displayOrder: DisplayOrder) => void
): IContextualMenuItem[] {
    if (isInSearch) {
        return createSearchOptionsItems(viewState, onFilterChanged);
    }

    const filterOptions: IContextualMenuItem[] = hideFilterFromDropDown
        ? []
        : [
              {
                  key: 'Filter',
                  itemType: ContextualMenuItemType.Header,
                  name: loc(filterLabel),
              },
              createIContextualMenuItem(
                  loc(allLabel),
                  viewState.filter === Filter.All,
                  () => onFilterChanged(Filter.All),
                  getFilterAriaLabel
              ),
              createIContextualMenuItem(
                  loc(showPeopleLabel),
                  viewState.filter === Filter.HubPerson,
                  () => onFilterChanged(Filter.Contact),
                  getFilterAriaLabel
              ),
              createIContextualMenuItem(
                  loc(showContactListsLabel),
                  viewState.filter === Filter.PrivateDistributionList,
                  () => onFilterChanged(Filter.PrivateDistributionList),
                  getFilterAriaLabel
              ),
          ];

    const groupFilter: IContextualMenuItem[] = showGroupsFromDropDown
        ? [
              createIContextualMenuItem(
                  loc(groupsFolder),
                  viewState.filter === Filter.Group,
                  () => onFilterChanged(Filter.Group),
                  getFilterAriaLabel
              ),
          ]
        : [];

    const sortingOptions: IContextualMenuItem[] = [
        {
            key: 'Sort order',
            itemType: ContextualMenuItemType.Header,
            name: loc(sortOrderLabel),
        },
    ];

    if (isInDeletedItems) {
        sortingOptions.push(
            createIContextualMenuItem(
                loc(sortByNameLabel),
                viewState.sorting === Sorting.ByName,
                () => onSortingChanged(Sorting.ByName),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByDeletedDateLabel),
                viewState.sorting === Sorting.ByDeletedDate,
                () => onSortingChanged(Sorting.ByDeletedDate),
                getSortByAriaLabel
            )
        );
    } else if (viewState.filter === Filter.PrivateDistributionList) {
        sortingOptions.push(
            createIContextualMenuItem(
                loc(sortByNameLabel),
                viewState.sorting === Sorting.ByName,
                () => onSortingChanged(Sorting.ByName),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByRecentlyAddedLabel),
                viewState.sorting === Sorting.ByRecentlyAdded,
                () => onSortingChanged(Sorting.ByRecentlyAdded),
                getSortByAriaLabel
            )
        );
    } else {
        sortingOptions.push(
            createIContextualMenuItem(
                loc(sortByFirstNameLabel),
                viewState.sorting === Sorting.ByFirstName,
                () => onSortingChanged(Sorting.ByFirstName),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByLastNameLabel),
                viewState.sorting === Sorting.ByLastName,
                () => onSortingChanged(Sorting.ByLastName),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByCompanyLabel),
                viewState.sorting === Sorting.ByCompany,
                () => onSortingChanged(Sorting.ByCompany),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByHomeCityLabel),
                viewState.sorting === Sorting.ByHomeCity,
                () => onSortingChanged(Sorting.ByHomeCity),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByWorkCityLabel),
                viewState.sorting === Sorting.ByWorkCity,
                () => onSortingChanged(Sorting.ByWorkCity),
                getSortByAriaLabel
            ),
            createIContextualMenuItem(
                loc(sortByRecentlyAddedLabel),
                viewState.sorting === Sorting.ByRecentlyAdded,
                () => onSortingChanged(Sorting.ByRecentlyAdded),
                getSortByAriaLabel
            )
        );
    }

    const filters = filterOptions.concat(groupFilter).concat(sortingOptions);

    return filters;
}
